<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex items-center">
            <img class="h-8 w-auto" src="/logo-mifo.svg" alt="MIFO" />
          </div>
          <div class="text-sm text-gray-500">
            Schritt {{ currentStep }} von 2
          </div>
        </div>
      </div>
    </header>

    <div class="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <!-- Progress Bar -->
      <div class="mb-8">
        <div class="flex items-start justify-between w-full">
          <!-- Step 1 -->
          <div class="flex flex-col items-center flex-1">
            <div class="rounded-full transition duration-500 ease-in-out h-12 w-12 py-3 border-2 flex items-center justify-center mb-2"
                 :class="currentStep >= 1 ? 'border-mifo-accent' : 'border-gray-300'">
              <span class="font-bold text-sm"
                    :class="currentStep >= 1 ? 'text-mifo-accent' : 'text-gray-500'">1</span>
            </div>
            <span class="text-sm font-medium text-center"
                  :class="currentStep >= 1 ? 'text-mifo-accent' : 'text-gray-500'">Buchung</span>
          </div>
          
          <!-- Step 2 -->
          <div class="flex flex-col items-center flex-1">
            <div class="rounded-full transition duration-500 ease-in-out h-12 w-12 py-3 border-2 flex items-center justify-center mb-2"
                 :class="currentStep >= 2 ? 'border-mifo-accent' : 'border-gray-300'">
              <span class="font-bold text-sm"
                    :class="currentStep >= 2 ? 'text-mifo-accent' : 'text-gray-500'">2</span>
            </div>
            <span class="text-sm font-medium text-center"
                  :class="currentStep >= 2 ? 'text-mifo-accent' : 'text-gray-500'">Zahlung</span>
          </div>
          

        </div>
      </div>

      <!-- Booking Form -->
      <div v-if="currentStep === 1" class="space-y-6">
        <div class="text-center mb-8">
          <h1 class="text-3xl font-heading font-bold text-mifo-primary mb-2">
            {{ course?.title }}
          </h1>
          <p class="text-lg text-gray-600">
            {{ course?.city || 'Laden...' }} • {{ course?.startDate ? formatDate(course.startDate) : 'Laden...' }} - {{ course?.endDate ? formatDate(course.endDate) : 'Laden...' }}
          </p>
          <p class="text-sm text-gray-500 mt-1">
            {{ availableSpots > 0 ? `${availableSpots} ${availableSpots === 1 ? 'Platz' : 'Plätze'} verfügbar` : 'Kurs ausgebucht' }}
          </p>
        </div>

        <!-- Waiting List Form (when course is sold out) -->
        <div v-if="availableSpots === 0" class="mt-8">
          <WaitingListForm 
            :course-id="route.params.courseId"
            @cancel="goBack"
            @success="handleWaitingListSuccess"
          />
        </div>

        <!-- All other sections (only show when course has available spots) -->
        <div v-if="availableSpots > 0">
          <PriceSelection v-model="selectedCourseType" :course="course" />

          <!-- Contact Information -->
          <div class="card">
            <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-6">Ihre Daten</h2>
            <form @submit.prevent="nextStep" class="space-y-6">
              <CustomerAddressForm v-model="customerForm" :title="'Privatanschrift'" :required="true" :showMedicalFields="true" />
              <div>
                <div class="flex items-center mb-4">
                  <input v-model="differentBilling" type="checkbox" id="differentBilling" class="h-4 w-4 text-mifo-accent focus:ring-mifo-accent border-gray-300 rounded" />
                  <label for="differentBilling" class="ml-2 text-sm text-gray-700">Rechnungsadresse abweichend von der Privatanschrift</label>
                </div>
                <BillingAddressForm v-if="differentBilling" v-model="billingForm" :enabled="differentBilling" :title="'Rechnungsadresse'" />
              </div>
              <LegalNotice />
            </form>
          </div>

          <!-- Add-on Products -->
          <div class="card">
            <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-6">
              Optionale Zusatzprodukte
            </h2>
            <div v-if="addons.loading" class="text-sm text-gray-500">Laden…</div>
            <div v-else-if="addons.items.length === 0" class="text-sm text-gray-500">Keine Zusatzprodukte verfügbar.</div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="p in addons.items"
                :key="p.id"
                class="border-2 rounded-lg p-4 transition-colors cursor-pointer"
                :class="addons.quantities[p.id] > 0 ? 'border-mifo-accent bg-mifo-accent/5' : 'border-gray-300 hover:border-gray-400'"
                @click="p.type === 'single' ? toggleSingleAddon(p.id) : null"
              >
                <div class="flex items-start justify-between">
                  <div class="pr-4">
                    <div class="font-medium text-gray-900">{{ p.name }}</div>
                    <div class="text-sm text-gray-600 mt-1" v-html="p.descriptionHtml"></div>
                  </div>
                  <div class="text-right text-mifo-primary font-semibold whitespace-nowrap">{{ formatCurrency(p.priceCents) }}</div>
                </div>
                
                <!-- Quantity selector only for multiple products -->
                <div v-if="p.type === 'multiple'" class="mt-3 flex items-center justify-between">
                  <span class="text-sm text-gray-600">Anzahl:</span>
                  <QuantitySelector 
                    v-model="addons.quantities[p.id]" 
                    :min-value="0"
                    @update:model-value="updateAddonQuantity(p.id, $event)"
                  />
                </div>
                
                <!-- No additional UI for single products - selection indicated by border/bg color -->
              </div>
            </div>
          </div>

          <CheckoutSummary v-model="selectedCourseType" :selectedAddons="selectedAddonsWithQuantities" :addonTotal="addonTotal" :sendingCosts="sendingCosts" :totalPrice="totalPrice" />

          <!-- Payment Method Selection -->
          <div class="card">
            <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-6">
              Zahlungsmethode wählen
            </h2>
            <PaymentMethodSelector v-model="selectedPaymentMethod" />
          </div>

          <div class="flex justify-end mt-4">
            <button
              @click="nextStep"
              :disabled="!selectedCourseType || !isFormValid || !selectedPaymentMethod"
              class="btn-primary px-8 py-3"
              :class="{ 'opacity-50 cursor-not-allowed': !selectedCourseType || !isFormValid || !selectedPaymentMethod }"
            >
              Sicher bezahlen
            </button>
          </div>
        </div>
            </div>

      <!-- Payment Redirect -->
      <div v-if="currentStep === 2" class="space-y-6">
        <div class="text-center mb-8">
          <h1 class="text-3xl font-heading font-bold text-mifo-primary mb-2">
            Zahlung
          </h1>
          <p class="text-lg text-gray-600">
            Sichere Zahlung über Mollie
          </p>
        </div>

        <div class="card">
          <div class="text-center">
            <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-mifo-accent mx-auto mb-4"></div>
            <p class="text-lg text-gray-600">
              Weiterleitung zur Zahlung...
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { formatCurrency, formatDate } from 'shared/utils'
// Removed FORM_FIELDS usage in favor of form components
import api from '@/utils/axios'
import WaitingListForm from '@/components/WaitingListForm.vue'
import PaymentMethodSelector from '@/components/PaymentMethodSelector.vue'
import LegalNotice from '@/components/LegalNotice.vue'
import CustomerAddressForm from '@/components/CustomerAddressForm.vue'
import BillingAddressForm from '@/components/BillingAddressForm.vue'
import PriceSelection from '@/components/PriceSelection.vue'
import CheckoutSummary from '@/components/CheckoutSummary.vue'
import QuantitySelector from '@/components/QuantitySelector.vue'

export default {
  name: 'Checkout',
  components: {
    WaitingListForm,
    PaymentMethodSelector,
    LegalNotice,
    CustomerAddressForm,
    BillingAddressForm,
    PriceSelection,
    CheckoutSummary,
    QuantitySelector
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const currentStep = ref(1) // Step 1: Booking, Step 2: Payment redirect
    const course = ref(null)
    
    // Course selection
    const selectedCourseType = ref(null)
    const selectedPaymentMethod = ref(null)

    
    // Customer form
    const customerForm = reactive({
      anrede: '',
      titel: '',
      firstName: '',
      lastName: '',
      fachrichtung: '',
      schaller: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: '',
      email: '',
      phone: ''
    })
    
    // Billing form
    const billingForm = reactive({
      institution: '',
      department: '',
      anrede: '',
      titel: '',
      firstName: '',
      lastName: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: ''
    })
    
    const differentBilling = ref(false)

    // Options provided by shared form components
    
    // Add-ons
    const addons = reactive({ loading: false, items: [], quantities: {} })
    const fetchAddons = async () => {
      try {
        addons.loading = true
        const { data } = await api.get('/products', { params: { addons: '1' } })
        addons.items = Array.isArray(data) ? data : []
        const quantities = {}
        addons.items.forEach(p => { 
          // All products start with quantity 0, user must select
          quantities[p.id] = 0
        })
        addons.quantities = quantities
      } catch (e) {
        addons.items = []
        addons.quantities = {}
      } finally {
        addons.loading = false
      }
    }
    const updateAddonQuantity = (id, quantity) => {
      addons.quantities[id] = quantity
    }
    
    const toggleSingleAddon = (id) => {
      const product = addons.items.find(p => p.id === id)
      if (product && product.type === 'single') {
        // Toggle between 0 and 1 for single products
        addons.quantities[id] = addons.quantities[id] > 0 ? 0 : 1
      }
    }

    // countries now come from shared constants via CustomerAddressForm/BillingAddressForm

    const availableSpots = computed(() => {
      if (!course.value) return 0
      const maxParticipants = course.value.maxParticipants || course.value.totalSpots
      const currentParticipants = course.value.currentParticipants || course.value.soldSpots
      return Math.max(0, maxParticipants - currentParticipants)
    })

    const totalPrice = computed(() => {
      if (!course.value) return 0
      
      let total = 0
      
      if (selectedCourseType.value === 'standard') {
        total += course.value.prices?.standard || 0
      } else if (selectedCourseType.value === 'assistenzarzt') {
        total += course.value.prices?.assistenzarzt || 0
      }
      return total + addonTotal.value + sendingCosts.value
    })

    const selectedAddons = computed(() => addons.items.filter(p => addons.quantities[p.id] > 0))
    const selectedAddonsWithQuantities = computed(() => 
      selectedAddons.value.map(p => ({ ...p, quantity: addons.quantities[p.id] }))
    )
    const addonTotal = computed(() => {
      if (!addons.items.length) return 0
      return addons.items.reduce((sum, p) => sum + (p.priceCents * addons.quantities[p.id]), 0)
    })

    const sendingCosts = computed(() => {
      if (!addons.items.length) return 0
      
      // Find the highest sending cost from selected add-ons
      let highestSendingCost = 0
      addons.items.forEach(p => {
        if (addons.quantities[p.id] > 0 && p.sendingCostsCents && p.sendingCostsCents > highestSendingCost) {
          highestSendingCost = p.sendingCostsCents
        }
      })
      
      return highestSendingCost
    })

    const isFormValid = computed(() => {
      return customerForm.anrede &&
             customerForm.firstName &&
             customerForm.lastName &&
             customerForm.fachrichtung &&
             customerForm.schaller &&
             customerForm.street &&
             customerForm.houseNumber &&
             customerForm.postalCode &&
             customerForm.city &&
             customerForm.country &&
             customerForm.email
    })

    const fetchCourse = async () => {
      try {
        const response = await api.get(`/courses/${route.params.courseId}`)
        course.value = response.data
      } catch (error) {
        console.error('Error fetching course:', error)
        // Mock data for now - use ECHO-001 as fallback
        course.value = {
          id: 'ECHO-001',
          title: 'Echokardiographie Grundkurs',
          startDate: '2024-02-15',
          endDate: '2024-02-17',
          city: 'Berlin',
          totalSpots: 20,
          soldSpots: 20, // Changed to make course sold out
          status: 'active',
          prices: {
            standard: 120000,
            assistenzarzt: 100000
          }
        }
      }
    }

    const goBack = () => {
      router.push('/courses')
    }

    const handleWaitingListSuccess = () => {
      // Show success message and redirect after a delay
      setTimeout(() => {
        router.push('/courses')
      }, 3000)
    }

    const nextStep = async () => {
      if (currentStep.value === 1) {
        // Submit order and advance to payment step
        try {
          const addonsPayload = selectedAddons.value.map(p => ({ productId: p.id, quantity: addons.quantities[p.id] }))
          const orderData = {
            type: 'course',
            courseId: course.value.id,
            courseType: selectedCourseType.value,
            courseAmount: selectedCourseType.value === 'standard' ? (course.value.prices?.standard || 0) : (course.value.prices?.assistenzarzt || 0),
            customer: customerForm,
            billing: differentBilling.value ? billingForm : null,
            addons: addonsPayload,
            paymentMethod: selectedPaymentMethod.value
          }
          
          const response = await api.post('/orders', orderData)
          
          // Advance to payment step
          currentStep.value = 2
          
          // Redirect to Mollie payment after a short delay
          setTimeout(() => {
            if (response.data.paymentUrl) {
              window.location.href = response.data.paymentUrl
            }
          }, 1000)
        } catch (error) {
          console.error('Error creating order:', error)
          
          // Handle specific error cases
          if (error.response?.status === 409) {
            // Course is sold out - refresh course data and show waiting list
            await fetchCourse()
            alert(error.response.data.message || 'Kurs ist ausgebucht')
          } else if (error.response?.status === 400) {
            alert(error.response.data.message || 'Kurs ist nicht verfügbar')
          } else if (error.response?.status === 404) {
            alert(error.response.data.message || 'Kurs nicht gefunden')
          } else {
            alert('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.')
          }
        }
      }
    }

    onMounted(() => {
      fetchCourse()
      fetchAddons()
    })

    return {
      route,
      currentStep,
      course,
      availableSpots,
      selectedCourseType,
      selectedPaymentMethod,
      customerForm,
      billingForm,
      differentBilling,
      addons,
      selectedAddons,
      selectedAddonsWithQuantities,
      addonTotal,
      sendingCosts,
      updateAddonQuantity,
      toggleSingleAddon,
      totalPrice,
      isFormValid,
      formatCurrency,
      formatDate,
      nextStep,
      goBack,
      handleWaitingListSuccess
    }
  }
}
</script> 